import { useState, useEffect } from 'react';
import { fetchQuizCategories, type QuizMeta } from '../../lib/quizData';
import { useSEO } from '../../hooks/useSEO';
import { Chip } from '../../components/shared/Chip';
import { ScriptText } from '../../components/shared/ScriptText';
import { Button } from '../../components/shared/Button';
import { StoreBadges } from '../../components/shared/StoreBadges';
import { PhoneMockup } from '../../components/shared/PhoneMockup';
import { QuizCategoryCard } from '../../components/quiz/QuizCategoryCard';
import quizBg from '../../assets/images/quiz-bg.png';
import styles from './QuizLanding.module.css';

export function QuizLanding() {
  useSEO({
    title: 'Couple Quiz — Discover Each Other\'s Fantasies | Free',
    description:
      'Take intimate quizzes together. Both Answer Quiz secretly, reveal only mutual fantasies. Build your Sex Bucket List. No app needed.',
    keywords:
      'couple quiz, intimacy quiz, sex bucket list, couples game, relationship quiz free',
  });

  const [categories, setCategories] = useState<QuizMeta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuizCategories()
      .then(setCategories)
      .finally(() => setLoading(false));
  }, []);

  const totalQuestions = categories.reduce((s, c) => s + c.questionCount, 0);
  const firstFree = categories.find((c) => !c.isLocked);

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${quizBg})` }}
      >
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <Chip>Free Couple Quiz</Chip>
          <h1 className={`display ${styles.heroTitle}`}>
            Discover each other's<br />
            <ScriptText rotate={-2}>fantasies.</ScriptText>
          </h1>
          <p className={styles.heroSub}>
            Take turns answering intimate questions. Only mutual desires are
            revealed. What one person says no to stays secret — forever.
          </p>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{categories.length}</span>
              <span className={styles.statLabel}>quizzes</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{totalQuestions}+</span>
              <span className={styles.statLabel}>intimate questions</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>100%</span>
              <span className={styles.statLabel}>secret</span>
            </div>
          </div>
          {firstFree && (
            <Button variant="primary" href={`/quiz/${firstFree.id}`}>
              ♥ Play Free →
            </Button>
          )}
        </div>
      </section>

      {/* Category grid */}
      <section className={styles.section}>
        <h2 className={`display ${styles.sectionTitle}`}>
          Choose your <ScriptText>heat.</ScriptText>
        </h2>
        {loading ? (
          <p className={styles.loading}>Loading quizzes...</p>
        ) : (
          <div className={styles.grid}>
            {categories.map((cat) => (
              <QuizCategoryCard key={cat.id} quiz={cat} />
            ))}
          </div>
        )}
      </section>

      {/* App promo banner */}
      <section className={styles.promoBanner}>
        <div className={styles.promoContent}>
          <h2 className={`display ${styles.promoTitle}`}>
            200+ intimate questions.<br />
            One Sex <ScriptText>Bucket List.</ScriptText>
          </h2>
          <p className={styles.promoSub}>
            Play every quiz, unlock all dares, read stories together — and
            build your ultimate bucket list in the app.
          </p>
          <StoreBadges analyticsLocation="quiz_landing" />
        </div>
        <div className={styles.promoPhone}>
          <PhoneMockup scale={0.55} />
        </div>
      </section>
    </div>
  );
}
